// `useSlideOutOnComplete` — keeps just-completed task ids in a transient
// "sliding-out" set for ~300 ms so the cooking pane can animate them
// out of view before they unmount.
//
// Usage: pass the latest set of completed task ids. The hook returns
//   - `slidingOut`: ids currently in the slide-out window. The pane
//     should keep rendering them with `slidingOut` set on the
//     TaskRectangle so the CSS class fires.
//
// Each id's removal timer is owned in a ref and fires exactly once,
// `SLIDE_OUT_MS` after the id first appears — independent of how often
// this hook re-runs. That matters because `completedIds` gets a fresh
// array identity on every tick (the classified plan is recomputed each
// second), so an effect that cancelled/rescheduled its timers on every
// run would clear an in-flight slide-out and never remove it — leaving the
// completed card as an invisible, space-occupying row (the slide-out CSS
// keeps the element's box via transform+opacity). See styles.css.
//
// The wall-clock window is fixed; reduced-motion is honoured by the CSS
// rule (the animation collapses to an immediate opacity:0).

import { useEffect, useRef, useState } from 'react';

const SLIDE_OUT_MS = 300;

export function useSlideOutOnComplete(completedIds: readonly string[]): {
  slidingOut: ReadonlySet<string>;
} {
  const knownRef = useRef<Set<string>>(new Set());
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [slidingOut, setSlidingOut] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const seen = knownRef.current;
    const newly: string[] = [];
    for (const id of completedIds) {
      if (!seen.has(id)) newly.push(id);
    }
    // Always resync the known set so a recipe reset (ids dropped) lets a
    // future re-completion re-trigger the animation.
    knownRef.current = new Set(completedIds);
    if (newly.length === 0) return;

    setSlidingOut((prev) => {
      const next = new Set(prev);
      for (const id of newly) next.add(id);
      return next;
    });
    // One self-clearing removal timer per newly-completed id, owned in a
    // ref so subsequent re-runs of this effect never cancel it.
    for (const id of newly) {
      const existing = timersRef.current.get(id);
      if (existing) clearTimeout(existing);
      const t = setTimeout(() => {
        timersRef.current.delete(id);
        setSlidingOut((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, SLIDE_OUT_MS);
      timersRef.current.set(id, t);
    }
  }, [completedIds]);

  // Clear any pending timers only on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  return { slidingOut };
}
